import React, { FC, useContext } from 'react'
import styled, { ThemeContext } from 'styled-components'

import * as PT from 'modules/Pricing/types'

import Spacer from 'components/Spacer'

import * as T from 'types'
import * as E from 'types/enums'

import Features from './Features'
import GridItemContainer from './GridItemContainer'
import PackageDisclaimer from './PackageDisclaimer'

const FeaturesContainer = styled.div`
  display: grid;
  grid-template-rows: repeat(4, auto);
  row-gap: 24px;

  @media (min-width: ${props => props.theme.metrics.desktop}px) {
    grid-template-rows: auto;
    grid-template-columns: repeat(4, minmax(200px, 1fr));
    row-gap: 0;
  }
`

const SectionTitle = styled.h5`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 39px;
  font-weight: 900;
  font-size: 20px;
  line-height: 27px;

  color: ${props => props.theme.colors.neutral800};
  background-color: ${props => props.theme.colors.neutral25};
  border-radius: 8px;
  margin: 0 32px;
`
interface IBoxShadow {
  boxShadow?: string
}
interface IBorderRadius {
  borderRadius?: string
}
interface IPackageType {
  packageType: E.PackageType
}
const getPadding = (props: IPackageType) => {
  if (props.packageType === E.PackageType.basic) return '20px 28px 40px 32px'

  if (props.packageType === E.PackageType.elite) return '20px 32px 40px 28px'

  return '20px 28px 40px'
}

const FeatureContainer = styled.div<
  Partial<PT.IPricingPackageCardModeProps> & IPackageType & IBoxShadow & IBorderRadius
>`
  color: ${props =>
    props.mode === E.PackageCardMode.Best
      ? props.theme.colors.white
      : props.theme.colors.neutral600};
  background-color: ${props =>
    props.mode === E.PackageCardMode.Best ? props.theme.colors.blue900 : 'none'};
  padding: ${props => getPadding(props)};

  ${props => props.boxShadow && `box-shadow: ${props.boxShadow}`};
  ${props => props.borderRadius && `border-radius: ${props.borderRadius}`};
  height: 100%;
`

const PackageDisclaimerContainer = styled.div`
  background-color: ${props => props.theme.colors.neutral25};
  margin: 0 32px;
  border-radius: 24px;
`

interface IProps {
  featuresExclusive: PT.IFeatures
  selectedState: T.IState | null
}

const PackageCardsFeaturesGrid: FC<IProps> = ({ featuresExclusive, selectedState }) => {
  const theme = useContext(ThemeContext)

  return (
    <FeaturesContainer>
      <GridItemContainer
        rowStart={1}
        rowEnd={9}
        columnStart={1}
        columnEnd={6}
        backgroundColor={theme.colors.white}
        desktopBorderRadius="0 0 24px 24px"
        hideOnMobile
      />
      <GridItemContainer
        rowStart={1}
        rowEnd={2}
        columnStart={1}
        columnEnd={6}
        zIndex={2}
        hideOnMobile
      >
        <SectionTitle>What You&apos;ll Get:</SectionTitle>
      </GridItemContainer>
      <GridItemContainer
        rowStart={2}
        rowEnd={3}
        columnStart={1}
        columnEnd={2}
        hideOnMobile
      >
        <FeatureContainer packageType={E.PackageType.basic}>
          <Features
            features={featuresExclusive.Basic}
            featureType={E.FeatureType.service}
            packageType={E.PackageType.basic}
          />
        </FeatureContainer>
      </GridItemContainer>
      <GridItemContainer
        rowStart={2}
        rowEnd={3}
        columnStart={2}
        columnEnd={3}
        hideOnMobile
      >
        <FeatureContainer packageType={E.PackageType.premium}>
          <Features
            features={featuresExclusive.Premium}
            featureType={E.FeatureType.service}
            packageType={E.PackageType.premium}
          />
        </FeatureContainer>
      </GridItemContainer>
      <GridItemContainer
        rowStart={2}
        rowEnd={3}
        columnStart={3}
        columnEnd={4}
        mode={E.PackageCardMode.Best}
        hideOnMobile
      >
        <FeatureContainer
          packageType={E.PackageType.platinum}
          mode={E.PackageCardMode.Best}
          boxShadow="0px 0px 48px rgba(1, 70, 112, 0.32)"
        >
          <Features
            features={featuresExclusive.Platinum}
            featureType={E.FeatureType.service}
            packageType={E.PackageType.platinum}
            mode={E.PackageCardMode.Best}
          />
        </FeatureContainer>
      </GridItemContainer>
      <GridItemContainer
        rowStart={2}
        rowEnd={3}
        columnStart={4}
        columnEnd={5}
        hideOnMobile
      >
        <FeatureContainer packageType={E.PackageType.elite}>
          <Features
            features={featuresExclusive.Elite}
            featureType={E.FeatureType.service}
            mode={E.PackageCardMode.ComingSoon}
            packageType={E.PackageType.elite}
          />
        </FeatureContainer>
      </GridItemContainer>
      <GridItemContainer
        rowStart={3}
        rowEnd={4}
        columnStart={1}
        columnEnd={6}
        zIndex={2}
        hideOnMobile
      >
        <SectionTitle>How We&apos;ll Help:</SectionTitle>
      </GridItemContainer>
      <GridItemContainer
        rowStart={5}
        rowEnd={6}
        columnStart={1}
        columnEnd={2}
        hideOnMobile
      >
        <FeatureContainer packageType={E.PackageType.basic}>
          <Features
            features={featuresExclusive.Basic}
            featureType={E.FeatureType.help}
            packageType={E.PackageType.basic}
          />
        </FeatureContainer>
      </GridItemContainer>
      <GridItemContainer
        rowStart={5}
        rowEnd={6}
        columnStart={2}
        columnEnd={3}
        hideOnMobile
      >
        <FeatureContainer packageType={E.PackageType.premium}>
          <Features
            features={featuresExclusive.Premium}
            featureType={E.FeatureType.help}
            packageType={E.PackageType.premium}
          />
        </FeatureContainer>
      </GridItemContainer>
      <GridItemContainer
        mode={E.PackageCardMode.Best}
        rowStart={5}
        rowEnd={6}
        columnStart={3}
        columnEnd={4}
        hideOnMobile
      >
        <FeatureContainer
          mode={E.PackageCardMode.Best}
          packageType={E.PackageType.platinum}
          boxShadow="0px 0px 48px rgba(1, 70, 112, 0.32)"
        >
          <Features
            features={featuresExclusive.Platinum}
            featureType={E.FeatureType.help}
            mode={E.PackageCardMode.Best}
            packageType={E.PackageType.platinum}
          />
        </FeatureContainer>
      </GridItemContainer>
      <GridItemContainer
        rowStart={5}
        rowEnd={6}
        columnStart={4}
        columnEnd={5}
        hideOnMobile
      >
        <FeatureContainer packageType={E.PackageType.elite}>
          <Features
            features={featuresExclusive.Elite}
            featureType={E.FeatureType.help}
            mode={E.PackageCardMode.ComingSoon}
            packageType={E.PackageType.elite}
          />
        </FeatureContainer>
      </GridItemContainer>
      <GridItemContainer
        rowStart={6}
        rowEnd={7}
        columnStart={1}
        columnEnd={6}
        zIndex={2}
        hideOnMobile
      >
        <SectionTitle>Support Available:</SectionTitle>
      </GridItemContainer>
      <GridItemContainer
        rowStart={7}
        rowEnd={8}
        columnStart={1}
        columnEnd={2}
        hideOnMobile
      >
        <FeatureContainer packageType={E.PackageType.basic}>
          <Features
            features={featuresExclusive.Basic}
            featureType={E.FeatureType.support}
            packageType={E.PackageType.basic}
          />
        </FeatureContainer>
      </GridItemContainer>
      <GridItemContainer
        rowStart={7}
        rowEnd={8}
        columnStart={2}
        columnEnd={3}
        hideOnMobile
      >
        <FeatureContainer packageType={E.PackageType.premium}>
          <Features
            features={featuresExclusive.Premium}
            featureType={E.FeatureType.support}
            packageType={E.PackageType.premium}
          />
        </FeatureContainer>
      </GridItemContainer>
      <GridItemContainer
        mode={E.PackageCardMode.Best}
        rowStart={7}
        rowEnd={8}
        columnStart={3}
        columnEnd={4}
        hideOnMobile
      >
        <FeatureContainer
          mode={E.PackageCardMode.Best}
          boxShadow="0px 24px 48px rgba(1, 70, 112, 0.32)"
          borderRadius="0 0 26px 26px"
          packageType={E.PackageType.platinum}
        >
          <Features
            features={featuresExclusive.Platinum}
            featureType={E.FeatureType.support}
            mode={E.PackageCardMode.Best}
            packageType={E.PackageType.platinum}
          />
        </FeatureContainer>
      </GridItemContainer>
      <GridItemContainer
        rowStart={7}
        rowEnd={8}
        columnStart={4}
        columnEnd={5}
        hideOnMobile
      >
        <FeatureContainer packageType={E.PackageType.elite}>
          <Features
            features={featuresExclusive.Elite}
            featureType={E.FeatureType.support}
            packageType={E.PackageType.elite}
            mode={E.PackageCardMode.ComingSoon}
          />
        </FeatureContainer>
      </GridItemContainer>
      <GridItemContainer
        rowStart={8}
        rowEnd={9}
        columnStart={1}
        columnEnd={6}
      >
        <Spacer size={55} />
        <PackageDisclaimerContainer>
          <PackageDisclaimer
            featuresExclusive={featuresExclusive}
            selectedState={selectedState}
          />
        </PackageDisclaimerContainer>
        <Spacer size={36} />
      </GridItemContainer>
    </FeaturesContainer>
  )
}

export default PackageCardsFeaturesGrid
